import { Button, Modal } from "@xola/ui-kit";
import React, { useState } from "react";
import { ProductObject, SelectionState } from "types";
import { ProductSelector, ProductSelectorProps } from "./ProductSelector";
import { SecondaryButton } from "./SecondaryButton";

type AllowedProductSelectorProps = Omit<
    ProductSelectorProps,
    "value" | "onChange" | "collections" | "className" | "classNames" | "extendedList"
>;

interface ProductSelectorModalProps extends AllowedProductSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    value: SelectionState;
    onConfirm: (value: SelectionState) => void;
    title?: string;
    description?: string;
    objects?: ProductObject[];
    allowEmptySelection?: boolean;
}

export const ProductSelectorModal = ({
    isOpen,
    onClose,
    value,
    onConfirm,
    title = "Select Products",
    description,
    objects,
    enableSelectAll,
    allowEmptySelection = false,
    hideUnScheduled = false,
    ...productSelectorProps
}: ProductSelectorModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent
                hideUnScheduled={hideUnScheduled}
                enableSelectAll={enableSelectAll}
                allowEmptySelection={allowEmptySelection}
                value={value}
                title={title}
                description={description}
                objects={objects}
                onConfirm={onConfirm}
                onClose={onClose}
                {...productSelectorProps}
            />
        </Modal>
    );
};

interface ModalContentProps extends Omit<ProductSelectorModalProps, "isOpen"> {}

const ModalContent = ({
    onClose,
    value,
    onConfirm,
    title,
    description,
    objects,
    allowEmptySelection,
    ...productSelectorProps
}: ModalContentProps) => {
    const [selectedProducts, setSelectedProducts] = useState<SelectionState>(value);

    const handleConfirmClick = () => {
        onConfirm(selectedProducts);
        onClose();
    };

    const isEmptySelection = !selectedProducts.all && selectedProducts.selected.length === 0;

    return (
        <>
            <Modal.Header description={description}>{title}</Modal.Header>

            <Modal.Body>
                <ProductSelector
                    value={value}
                    collections={objects}
                    onChange={setSelectedProducts}
                    {...productSelectorProps}
                />
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <Button disabled={!allowEmptySelection && isEmptySelection} onClick={handleConfirmClick}>
                    Confirm
                </Button>
            </Modal.Footer>
        </>
    );
};
